import { userHasOnlySessionCarts } from '@ab-core-functions/user-permission';
import { BASIC_CHECKBOX_SIZE, BasicCheckbox } from '@ab-core/checkbox';
import Headline from '@ab-core/headline';
import { useShoppingLists } from '@ab-core/hooks-v2';
import { useMyCarts, useReceivedCarts, useSharedCarts } from '@ab-core/hooks/cartNew/getCarts';
import { Icon } from '@ab-core/icon';
import { Loader } from '@ab-core/loader';
import Spacer from '@ab-core/spacer';
import type { TabProps, TabsType } from '@ab-core/tabs';
import { Tabs } from '@ab-core/tabs';
import { getTestIdProp } from '@ab-core/testing';
import Text from '@ab-core/text';
import type { FC } from 'react';
import React, { useEffect, useState } from 'react';
import { Border, List } from './styled';
import type { TabInfoTextProps } from './tabsText';
import { CartInfoText, ShoppinglistInfoText } from './tabsText';

export type CartListModalTabsProps = {
    isUnqualifiedContractProduct: boolean;
    onCheckCarts: (ids: Array<string>) => void;
    onCheckLists: (ids: Array<string>) => void;
    onClose: TabInfoTextProps['onClose'];
};

export const CartListModalTabs: FC<CartListModalTabsProps> = ({
    isUnqualifiedContractProduct = false,
    onCheckCarts,
    onCheckLists,
    onClose
}) => {
    const { myCarts, myCartsLoading } = useMyCarts(true);
    const { receivedCarts, receivedCartsLoading } = useReceivedCarts(true);
    const { sharedCarts, sharedCartsLoading } = useSharedCarts(true);
    const { loading: shoppingListsLoading, shoppingLists } = useShoppingLists({});
    const allCarts = [...(myCarts || []), ...(receivedCarts || []), ...(sharedCarts || [])];
    const showIfNotOciSession = userHasOnlySessionCarts();
    const [cartIds, setCartIds] = useState<Array<string>>([]);
    const [listIds, setListIds] = useState<Array<string>>([]);
    const [activeTab, setActiveTab] = useState(0);

    const handleCheckBoxCarts = (id: string, e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.currentTarget.checked;

        if (isChecked) {
            setCartIds((currIds) => [...currIds, id]);
        } else {
            setCartIds((currIds) => currIds.filter((currId) => currId !== id));
        }
    };
    const handleCheckBoxShoppingLists = (id: string, e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.currentTarget.checked;

        if (isChecked) {
            setListIds((currIds) => [...currIds, id]);
        } else {
            setListIds((currIds) => currIds.filter((currId) => currId !== id));
        }
    };

    const onTabClick = (index: number) => {
        setActiveTab(index);
        setListIds([]);
        setCartIds([]);
    };

    useEffect(() => {
        onCheckCarts(cartIds);
        onCheckLists(listIds);
    }, [cartIds, listIds]);

    const tabs: TabsType = [];

    if (!isUnqualifiedContractProduct) {
        const tab: TabProps = {
            head: (
                <>
                    <Icon name="Cart" />
                    <Text color="inherit" {...getTestIdProp('tab-button-warenkoerbe')}>
                        Warenkörbe
                    </Text>
                </>
            )
        };

        if (showIfNotOciSession) {
            tab.content = (
                <>
                    <List {...getTestIdProp('cart-modal-checkbox-list')}>
                        {allCarts?.map((cart) => (
                            <li key={cart?.id}>
                                <BasicCheckbox
                                    {...getTestIdProp('cart-modal-checkbox')}
                                    onChange={(e) => handleCheckBoxCarts(cart?.id, e)}
                                    checked={cartIds?.includes(cart?.id)}
                                    label={
                                        cart?.custom?.customFieldsRaw?.find(
                                            (customFieldRaw) => customFieldRaw.name === 'cart-name'
                                        )?.value
                                    }
                                    inputSize={BASIC_CHECKBOX_SIZE.BASIC}
                                />
                            </li>
                        ))}
                    </List>
                    <Spacer size="sm" />
                    <CartInfoText onClose={onClose} />
                </>
            );
        }

        tabs.push(tab);
    }

    const shoppinglistTab: TabProps = {
        head: (
            <>
                <Icon name="Bookmarks" />
                <Text color="inherit" {...getTestIdProp('tab-button-merklisten')}>
                    Merklisten
                </Text>
            </>
        ),
        content: (
            <>
                <List {...getTestIdProp('all-shopping-list-modal-checkboxes')}>
                    {shoppingLists?.map((shoppingList) => (
                        <li key={shoppingList.id} {...getTestIdProp('shopping-list-modal-checkbox-listelement')}>
                            <BasicCheckbox
                                {...getTestIdProp('shopping-list-modal-checkbox')}
                                onChange={(e) => handleCheckBoxShoppingLists(shoppingList.id, e)}
                                checked={listIds?.includes(shoppingList?.id)}
                                label={shoppingList.name}
                                inputSize={BASIC_CHECKBOX_SIZE.BASIC}
                            />
                        </li>
                    ))}
                </List>
                <Spacer size="sm" />
                <ShoppinglistInfoText onClose={onClose} />
            </>
        )
    };
    tabs.push(shoppinglistTab);

    return (
        <>
            {myCartsLoading || receivedCartsLoading || sharedCartsLoading || shoppingListsLoading ? (
                <>
                    <Border>
                        <Loader />
                        <Headline>Lade...</Headline>
                    </Border>
                </>
            ) : (
                <>
                    <Tabs activeTab={activeTab} tabs={tabs} tabClicked={onTabClick} />
                </>
            )}
        </>
    );
};
